import React, { useState, useEffect } from 'react';
import {
  Users, TrendingUp, Clock, RefreshCw,
  Calendar, UserCheck, Inbox, ChevronDown,
} from 'lucide-react';
import { StatsCard } from '../../components/dashboard';
import { Card, Loading } from '../../components/common';
import useAuth from '../../hooks/useAuth';
import useBot from '../../hooks/useBot';
import conversationsService from '../../services/api/conversations.service';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const formatSafeDate = (dateString) => {
  try {
    if (!dateString) return 'Data não disponível';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Data inválida';
    return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
  } catch { return 'Data inválida'; }
};

const isToday = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const BOT_CONFIG = {
  ebes_bacabal: {
    specialists:  ['Zé Filho'],
    isHandedOff:  (conv) => !!conv.context?.handoff_summary,
    getSpecialist:(conv) => conv.context?.handoff_summary ? 'Zé Filho' : null,
  },
};

const DEFAULT_BOT_CONFIG = {
  specialists:  ['Silvia', 'Arimatea'],
  isHandedOff:  (conv) => !!conv.context?.especialista,
  getSpecialist:(conv) => conv.context?.especialista || null,
};

const getBotConfig = (botId) => BOT_CONFIG[botId] || DEFAULT_BOT_CONFIG;

const SPECIALIST_CONFIG = {
  Silvia: {
    ring:   'bg-violet-50 dark:bg-violet-900/20',
    icon:   'text-violet-600 dark:text-violet-400',
    bar:    'bg-violet-500',
    badge:  'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300',
    border: 'border-violet-200 dark:border-violet-800/40',
  },
  Arimatea: {
    ring:   'bg-orange-50 dark:bg-orange-900/20',
    icon:   'text-orange-600 dark:text-orange-400',
    bar:    'bg-orange-500',
    badge:  'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
    border: 'border-orange-200 dark:border-orange-800/40',
  },
  'Zé Filho': {
    ring:   'bg-emerald-50 dark:bg-emerald-900/20',
    icon:   'text-emerald-600 dark:text-emerald-400',
    bar:    'bg-emerald-500',
    badge:  'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
    border: 'border-emerald-200 dark:border-emerald-800/40',
  },
};

const DEFAULT_SPECIALIST_CONFIG = {
  ring:   'bg-slate-50 dark:bg-slate-700',
  icon:   'text-slate-500',
  bar:    'bg-slate-400',
  badge:  'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
  border: 'border-slate-200 dark:border-slate-700',
};

const getSpecialistConfig = (name) => SPECIALIST_CONFIG[name] || DEFAULT_SPECIALIST_CONFIG;

const buildMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
      fromDate: new Date(d.getFullYear(), d.getMonth(), 1),
      toDate:   new Date(d.getFullYear(), d.getMonth() + 1, 1),
    });
  }
  return options;
};

const MONTH_OPTIONS = buildMonthOptions();

const SalesDashboard = () => {
  const { user } = useAuth();
  const { selectedBotId, selectedBot } = useBot();

  const [loading, setLoading]             = useState(true);
  const [refreshing, setRefreshing]       = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[0].value);
  const [stats, setStats] = useState({
    total: 0, today: 0, waitingSpecialist: 0,
    specialistCounts: {}, inTriage: 0,
  });
  const [recentHandoffs, setRecentHandoffs] = useState([]);

  const currentMonthOption = MONTH_OPTIONS.find((o) => o.value === selectedMonth) || MONTH_OPTIONS[0];
  const isCurrentMonth = currentMonthOption.value === MONTH_OPTIONS[0].value;

  useEffect(() => {
    if (selectedBotId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBotId, selectedMonth]);

  const loadData = async () => {
    try {
      setLoading(true);
      const botCfg = getBotConfig(selectedBotId);
      const conversations = await conversationsService.getAllConversations(selectedBotId, {
        status: 'all',
        fromDate: currentMonthOption.fromDate,
        toDate:   currentMonthOption.toDate,
      });

      const handedOff = conversations.filter((c) => botCfg.isHandedOff(c));
      const inTriage  = conversations.filter((c) => !botCfg.isHandedOff(c));

      const specialistCounts = {};
      botCfg.specialists.forEach((s) => {
        specialistCounts[s] = handedOff.filter((c) => botCfg.getSpecialist(c) === s).length;
      });

      setStats({
        total:             conversations.length,
        today:             conversations.filter((c) => isToday(c.lastInboundAt || c.createdAt)).length,
        waitingSpecialist: handedOff.length,
        specialistCounts,
        inTriage:          inTriage.length,
      });

      setRecentHandoffs(
        handedOff
          .sort((a, b) => new Date(b.lastInboundAt || b.createdAt) - new Date(a.lastInboundAt || a.createdAt))
          .slice(0, 5)
      );
    } catch (err) {
      console.error('Erro ao carregar dados de vendas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" text="Carregando dashboard de vendas..." />
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-violet-500 to-purple-600 p-6 text-white shadow-lg">
        <div className="pointer-events-none absolute -right-16 -top-16 w-64 h-64 rounded-full bg-white/5" />
        <div className="pointer-events-none absolute -right-4 -bottom-8 w-40 h-40 rounded-full bg-white/5" />

        <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <p className="text-violet-200 text-sm font-medium mb-1">
              {selectedBot?.name || 'Bot de Vendas'}
            </p>
            <h1 className="text-2xl font-bold font-display">
              {getGreeting()}, {user?.name?.split(' ')[0] || 'Usuário'}
            </h1>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/60 pointer-events-none" />
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="pl-9 pr-7 py-2 bg-white/15 hover:bg-white/25 border border-white/20 rounded-lg text-xs font-medium text-white focus:outline-none appearance-none cursor-pointer transition-colors"
              >
                {MONTH_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value} className="text-slate-900 bg-white">
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-white/60 pointer-events-none" />
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 bg-white/15 hover:bg-white/25 rounded-lg border border-white/20 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
          {[
            { label: 'Total',                                value: stats.total },
            { label: isCurrentMonth ? 'Hoje' : 'No mês',   value: stats.today },
            { label: 'Com Especialista',                     value: stats.waitingSpecialist },
            { label: 'Em Triagem',                           value: stats.inTriage },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white/10 backdrop-blur-sm rounded-xl p-3.5 border border-white/10">
              <p className="text-white/70 text-xs mb-1">{label}</p>
              <p className="text-2xl font-bold font-display">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatsCard
          title="Total de Conversas"
          value={stats.total}
          icon={Users}
          subtitle="no período selecionado"
          color="primary"
        />
        <StatsCard
          title="Aguardando Especialista"
          value={stats.waitingSpecialist}
          icon={Clock}
          subtitle="aguardando resposta"
          color="warning"
          percentage={stats.total > 0 ? Math.round((stats.waitingSpecialist / stats.total) * 100) : 0}
        />
        <StatsCard
          title="Em Triagem"
          value={stats.inTriage}
          icon={Inbox}
          subtitle="no fluxo do bot"
          color="info"
          percentage={stats.total > 0 ? Math.round((stats.inTriage / stats.total) * 100) : 0}
        />
      </div>

      {/* Specialists */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {getBotConfig(selectedBotId).specialists.map((specialist) => {
          const count = stats.specialistCounts?.[specialist] || 0;
          const pct   = stats.waitingSpecialist > 0
            ? Math.round((count / stats.waitingSpecialist) * 100)
            : 0;
          const cfg = getSpecialistConfig(specialist);

          return (
            <Card key={specialist}>
              <Card.Body>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 ${cfg.ring} rounded-xl flex items-center justify-center`}>
                      <UserCheck className={`w-5 h-5 ${cfg.icon}`} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Especialista</p>
                      <p className="text-lg font-bold text-slate-900 dark:text-slate-100 font-display leading-tight">
                        {specialist}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-slate-900 dark:text-slate-100 font-display">{count}</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500">leads ativos</p>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-xs text-slate-400 dark:text-slate-500 mb-1.5">
                    {pct}% dos leads com especialista
                  </p>
                  <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${cfg.bar} rounded-full transition-all duration-700`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              </Card.Body>
            </Card>
          );
        })}
      </div>

      {/* Recent handoffs */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <div>
              <Card.Title>Últimos Leads Direcionados</Card.Title>
              <Card.Description>Leads encaminhados para um especialista recentemente</Card.Description>
            </div>
            <TrendingUp className="w-4 h-4 text-primary-500" />
          </div>
        </Card.Header>
        <Card.Body>
          {recentHandoffs.length === 0 ? (
            <div className="text-center py-10">
              <Users className="w-12 h-12 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
              <p className="text-sm text-slate-400 dark:text-slate-500">Nenhum lead direcionado recentemente</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentHandoffs.map((conv) => {
                const specialist = getBotConfig(selectedBotId).getSpecialist(conv);
                const cfg = getSpecialistConfig(specialist);
                return (
                  <div
                    key={conv.id}
                    className={`flex items-center justify-between p-3.5 rounded-xl border ${cfg.border} bg-slate-50/50 dark:bg-slate-800/40 hover:bg-slate-50 dark:hover:bg-slate-800/70 transition-colors`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 ${cfg.ring} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Users className={`w-3.5 h-3.5 ${cfg.icon}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                          {conv.lead?.name || conv.context?.nome || 'Lead'}
                        </p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 truncate">
                          {conv.lead?.phone || conv.protocol}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {specialist && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.badge}`}>
                          {specialist}
                        </span>
                      )}
                      <p className="text-xs text-slate-400 dark:text-slate-500 hidden sm:block">
                        {formatSafeDate(conv.lastInboundAt || conv.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default SalesDashboard;
